import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { ApiError } from '../../lib/api';
import { formatTimestampUs } from '../../lib/format';
import { listHelpdeskOperators, setHelpdeskOperator, type HelpdeskOperatorGrant } from './HelpdeskGovernanceApi';
import '../_shared/shared.css';
import './helpdeskGovernance.css';

const CAPABILITIES = [
  ['helpdesk:query', '案件與遮罩終端帳號全貌'],
  ['helpdesk:logs', '案件範圍診斷紀錄'],
  ['helpdesk:maintain', '需雙人核准的維護提案'],
  ['helpdesk:tunnel', '短效、命令白名單 reverse tunnel'],
  ['helpdesk:admin', 'Lead 指派、核准與治理'],
] as const;

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return `${error.code}: ${error.message}${error.requestId ? `（${error.requestId}）` : ''}`;
  return error instanceof Error ? error.message : '操作失敗';
}

export default function HelpdeskGovernancePage() {
  const { role } = useAuth();
  const owner = role === 'platform-owner';
  const [items, setItems] = useState<HelpdeskOperatorGrant[] | null>(null);
  const [loadError, setLoadError] = useState<Error | ApiError | null>(null);
  const [userId, setUserId] = useState('');
  const [scopes, setScopes] = useState<string[]>(['helpdesk:query', 'helpdesk:logs']);
  const [status, setStatus] = useState<'active' | 'disabled'>('active');
  const [reason, setReason] = useState('');
  // Step-up credential. Held only until submit resolves, then cleared — a
  // password left in component state outlives the action it authorized.
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState('');

  const load = useCallback(async () => {
    if (!owner) return;
    setLoadError(null);
    try {
      setItems(await listHelpdeskOperators());
    } catch (error) {
      setLoadError(error instanceof Error ? error : new Error('載入失敗'));
      setItems(null);
    }
  }, [owner]);

  useEffect(() => { void load(); }, [load]);

  function edit(item: HelpdeskOperatorGrant) {
    setUserId(item.user_id);
    setScopes(item.granted_scopes);
    setStatus(item.status === 'disabled' ? 'disabled' : 'active');
    setReason('例行 Helpdesk 權限檢視');
    setFeedback('');
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!owner || !userId.trim() || !reason.trim() || !password || (status === 'active' && scopes.length === 0)) return;
    setBusy(true); setFeedback('');
    try {
      const updated = await setHelpdeskOperator({ userId: userId.trim(), scopes, status, reason: reason.trim(), password });
      setFeedback(`已更新 ${updated.user_id}；伺服器已寫入不可變稽核紀錄。`);
      await load();
    } catch (error) {
      setFeedback(errorMessage(error));
    } finally {
      // Cleared on failure too: a rejected step-up must not leave the password
      // sitting in the form for the next person at this console.
      setPassword('');
      setBusy(false);
    }
  }

  if (!owner) {
    return <div className="state-block"><div className="state-title">無權限</div><div className="state-detail">只有 platform-owner 可檢視與變更 Helpdesk operator grant；客服作業請使用獨立 Helpdesk Console。</div></div>;
  }

  return (
    <div className="helpdesk-governance-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">平台治理</p>
          <h1>維修與客服治理</h1>
          <p className="page-subtitle">只管理 operator 的即時 capability grant 與稽核入口；案件、診斷、維護與 tunnel 執行不在 Admin Console。</p>
        </div>
        <Link className="btn-ghost" to="/audit?target_id=helpdesk_operator%3A">查詢 Helpdesk 稽核</Link>
      </header>

      <section className="card helpdesk-policy-card">
        <h2>強制政策</h2>
        <ul>
          <li>所有作業必須綁定案件；終端帳號全貌只回傳遮罩資料。</li>
          <li>裝置診斷、維護與反向通道需不同主管核准，短效簽章且可撤回。</li>
          <li>診斷資料預設 7 天刪除；legal hold 只凍結案件範圍的不可變物件。</li>
          <li>舊的管理端強制解綁／釋放路徑已移除，不能繞過 Helpdesk 的雙人核准。</li>
        </ul>
      </section>

      <form className="card helpdesk-grant-form" onSubmit={(event) => void submit(event)}>
        <h2>維運者授權</h2>
        <p className="text-secondary">帳號須先在「維運團隊」授予 helpdesk-agent 或 helpdesk-lead；伺服器會依角色裁切可授權範圍。</p>
        <label className="adm-field">
          <span>使用者 ID</span>
          <input value={userId} onChange={(event) => setUserId(event.target.value)} placeholder="usr-…" required />
        </label>
        <fieldset className="helpdesk-scope-grid">
          <legend>能力範圍</legend>
          {CAPABILITIES.map(([scope, label]) => (
            <label key={scope}>
              <input type="checkbox" checked={scopes.includes(scope)} onChange={(event) => {
                setScopes((current) => event.target.checked ? [...current, scope] : current.filter((item) => item !== scope));
              }} />
              <span><code>{scope}</code><small>{label}</small></span>
            </label>
          ))}
        </fieldset>
        <label className="adm-field">
          <span>授權狀態</span>
          <select value={status} onChange={(event) => setStatus(event.target.value as 'active' | 'disabled')}>
            <option value="active">active</option>
            <option value="disabled">停用（立即撤權）</option>
          </select>
        </label>
        <label className="adm-field">
          <span>變更原因（寫入稽核）</span>
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} required />
        </label>
        <label className="adm-field">
          <span>本人密碼（二次驗證）</span>
          <input type="password" autoComplete="current-password" value={password}
            onChange={(event) => setPassword(event.target.value)} required />
          <small className="text-secondary">授予能力範圍需重新驗證身分；密碼只送出此次請求，不寫入稽核。</small>
        </label>
        <button className="btn-primary" disabled={busy || !userId.trim() || !reason.trim() || !password || (status === 'active' && scopes.length === 0)}>
          {busy ? '儲存中…' : '儲存治理設定'}
        </button>
        {feedback && <p role="status">{feedback}</p>}
      </form>

      <section className="card">
        <div className="section-head"><h2>目前授權</h2><button className="btn-ghost btn-sm" onClick={() => void load()}>重新整理</button></div>
        {items === null && !loadError && <LoadingState label="載入 Helpdesk grants…" />}
        {loadError && <ErrorState error={loadError} onRetry={() => void load()} />}
        {items?.length === 0 && <EmptyState title="尚未建立 Helpdesk 授權" detail="先於維運團隊建立 helpdesk-agent / helpdesk-lead，再在本頁授予最小 capabilities。" />}
        {items && items.length > 0 && (
          <div className="table-scroll"><table className="data-table"><thead><tr><th>維運者</th><th>狀態</th><th>能力範圍</th><th>更新</th><th>治理</th></tr></thead>
            <tbody>{items.map((item) => <tr key={item.user_id}>
              <td className="adm-mono">{item.user_id}</td><td><span className={`pill ${item.status === 'active' ? 'pill-good' : 'pill-neutral'}`}>{item.status}</span></td>
              <td>{item.granted_scopes.map((scope) => <code key={scope} className="helpdesk-scope-chip">{scope}</code>)}</td>
              <td>{formatTimestampUs(item.updated_at)}</td>
              <td><button className="btn-ghost btn-sm" onClick={() => edit(item)}>編輯</button> <Link className="btn-ghost btn-sm" to={`/audit?target_id=${encodeURIComponent(`helpdesk_operator:${item.user_id}`)}`}>稽核</Link></td>
            </tr>)}</tbody></table></div>
        )}
      </section>
    </div>
  );
}
